import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import StaffHTTPService from 'services/HTTPService/staff/StaffHTTPService';

import {
    AutomaticCheckJournalId,
    AutomaticCheckResponsiblePersonDTO,
} from 'types/automaticCheckJournal';
import { useJournalId } from 'hooks/useJournalId/useJournalId';
import { JournalPagePropsWithJournalType } from 'pages/app/staff/journal/journalPagePropsWithJournalType';
import { useAppMutation } from 'services/reactQuery/useAppMutation';
import { IconChevronDown, IconChevronUp, IconSend } from '@tabler/icons-react';
import { useJournalDetailsBreadcrumb } from '../../../hooks';
import { Details } from '../../../../../../../../components/organisms';
import { ErrorDisplay } from '../../../../../../../../components/molecules';
import { commonTexts, journalTexts } from '../../../../../../../../consts/text';
import { useAppQuery } from '../../../../../../../../services/reactQuery/useAppQuery';
import { AppButton, AppParagraph } from '../../../../../../../../components/atoms';
import { parseStringIntoFormattedStringDate } from '../../../../../../../../utils/parsers/dateTime/parseStringIntoFormattedStringDate';
import { parseIssueHeader } from '../AutomaticCheckJournalUtil';

type ResponsiblePersonsTextProps = {
    responsiblePersons: AutomaticCheckResponsiblePersonDTO[];
};

const ResponsiblePersonsText = ({ responsiblePersons }: ResponsiblePersonsTextProps) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [hasOverflow, setHasOverflow] = useState(false);
    const textContainerRef = useRef<HTMLDivElement>(null);
    const responsiblePersonsNames = responsiblePersons
        .map((responsiblePerson) => responsiblePerson.fullName)
        .join(', ');

    useEffect(() => {
        const updateOverflowState = () => {
            if (isExpanded) {
                setHasOverflow(true);
                return;
            }

            const textContainer = textContainerRef.current?.querySelector(
                '.ant-typography',
            ) as HTMLElement | null;

            setHasOverflow(
                Boolean(
                    textContainer &&
                        Math.ceil(textContainer.scrollWidth) > Math.ceil(textContainer.clientWidth),
                ),
            );
        };

        updateOverflowState();
        window.addEventListener('resize', updateOverflowState);

        return () => window.removeEventListener('resize', updateOverflowState);
    }, [isExpanded, responsiblePersonsNames]);

    if (!responsiblePersonsNames) {
        return null;
    }

    return (
        <div
            style={{
                display: 'flex',
                alignItems: isExpanded ? 'flex-start' : 'center',
                gap: 4,
            }}
        >
            <div ref={textContainerRef} style={{ flex: 1, minWidth: 0 }}>
                <AppParagraph
                    textType="BodyMediumSemiBold"
                    marginBottom={0}
                    style={
                        isExpanded
                            ? undefined
                            : {
                                  display: 'block',
                                  width: '100%',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                              }
                    }
                >
                    {responsiblePersonsNames}
                </AppParagraph>
            </div>
            {hasOverflow && (
                <AppButton
                    type="link"
                    margin={0}
                    accessibilityLabel={isExpanded ? 'Zwiń listę osób' : 'Pokaż więcej osób'}
                    icon={isExpanded ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />}
                    onClick={() => setIsExpanded((currentValue) => !currentValue)}
                />
            )}
        </div>
    );
};

export const AutomaticCheckJournalDetailsPage = ({
    journalType,
}: JournalPagePropsWithJournalType) => {
    const { journalId } = useJournalId();
    const params = useParams();

    const automaticCheckJournalId = params.automaticCheckJournalId as
        | AutomaticCheckJournalId
        | undefined;
    const isAutomaticCheckJournalIdMissing = !automaticCheckJournalId;

    useJournalDetailsBreadcrumb({
        journalId,
        journalType,
    });

    const {
        data: automaticCheckJournalData,
        isFetching: isAutomaticCheckJournalDataFetching,
        isError: isAutomaticCheckJournalDataError,
    } = useAppQuery(
        'AUTOMATIC_CHECK_JOURNAL',
        [journalId, automaticCheckJournalId],
        () =>
            StaffHTTPService.automaticCheckJournal.getAutomaticCheckJournal(
                automaticCheckJournalId!,
            ),
        {
            enabled: !isAutomaticCheckJournalIdMissing,
        },
    );

    const isAutomaticCheckJournalDataMissing =
        !isAutomaticCheckJournalDataFetching &&
        !isAutomaticCheckJournalDataError &&
        !isAutomaticCheckJournalIdMissing &&
        !automaticCheckJournalData;
    const shouldShowInvalidDetailsState =
        isAutomaticCheckJournalIdMissing || isAutomaticCheckJournalDataMissing;

    const {
        mutate: automaticCheckJournalSendNotification,
        isLoading: addAutomaticCheckJournalSendNotificationLoading,
    } = useAppMutation(
        StaffHTTPService.automaticCheckJournal.automaticCheckJournalSendNotification,
        {
            key: ['AUTOMATIC_CHECK_JOURNAL_SEND_NOTIFICATION'],
            invalidateQueryKeys: [['AUTOMATIC_CHECK_JOURNAL', journalId, automaticCheckJournalId]],
        },
    );

    const appButtonText = automaticCheckJournalData?.notificationsSent
        ? journalTexts.journalDetails.common.automaticCheckJournal.details.notificationHasBeenSent
        : journalTexts.journalDetails.common.automaticCheckJournal.details.sendNotification;
    const automaticCheckJournalIssues = automaticCheckJournalData?.issues || [];
    const isAutomaticCheckJournalFailed = automaticCheckJournalData?.progressStatus === 'FAILED';
    const shouldShowSummaryOnly =
        isAutomaticCheckJournalFailed || automaticCheckJournalIssues.length === 0;

    return (
        <Details
            title={journalTexts.journalDetails.common.automaticCheckJournal.details.details}
            isLoading={isAutomaticCheckJournalDataFetching}
            isError={isAutomaticCheckJournalDataError && !shouldShowInvalidDetailsState}
        >
            {shouldShowInvalidDetailsState ? (
                <ErrorDisplay
                    title="Nie udało się otworzyć szczegółów sprawdzenia dziennika"
                    subTitle="Wróć do listy sprawdzeń i otwórz szczegóły ponownie."
                />
            ) : (
                <>
                    <Details.Item
                        title={
                            journalTexts.journalDetails.common.automaticCheckJournal.details.period
                        }
                    >
                        {automaticCheckJournalData &&
                            `${parseStringIntoFormattedStringDate(
                                automaticCheckJournalData.from,
                            )} - ${parseStringIntoFormattedStringDate(
                                automaticCheckJournalData.to,
                            )}`}
                    </Details.Item>
                    <Details.Item title={commonTexts.dataLabels.date}>
                        {automaticCheckJournalData &&
                            parseStringIntoFormattedStringDate(
                                automaticCheckJournalData.executedAt,
                            )}
                    </Details.Item>
                    <Details.Item
                        title={
                            journalTexts.journalDetails.common.automaticCheckJournal.details
                                .checkedBy
                        }
                    >
                        {automaticCheckJournalData?.executedByName}
                    </Details.Item>
                    <Details.Item
                        title={
                            journalTexts.journalDetails.common.automaticCheckJournal.details
                                .describe
                        }
                    >
                        {shouldShowSummaryOnly ? (
                            <AppParagraph>{automaticCheckJournalData?.summary}</AppParagraph>
                        ) : (
                            automaticCheckJournalIssues.map((issue) => {
                                return (
                                    <div
                                        key={`${issue.type}-${
                                            issue.journalEntryId || issue.message
                                        }`}
                                    >
                                        <AppParagraph textType="BodyMediumSemiBold">
                                            {parseIssueHeader(issue)}
                                        </AppParagraph>
                                        <AppParagraph>{issue.message}</AppParagraph>
                                        <ResponsiblePersonsText
                                            responsiblePersons={issue.responsiblePersons}
                                        />
                                        <hr style={{ opacity: 0.35 }} />
                                    </div>
                                );
                            })
                        )}
                    </Details.Item>
                    {!isAutomaticCheckJournalFailed && (
                        <AppButton
                            type="primary"
                            icon={<IconSend size={20} />}
                            margin={0}
                            onClick={() =>
                                automaticCheckJournalSendNotification({
                                    journalId,
                                    automaticCheckJournalId,
                                })
                            }
                            loading={addAutomaticCheckJournalSendNotificationLoading}
                            accessibilityLabel={appButtonText}
                            disabled={automaticCheckJournalData?.notificationsSent}
                        >
                            {appButtonText}
                        </AppButton>
                    )}
                </>
            )}
        </Details>
    );
};
